import { type FC, type FocusEvent, useState } from 'react'

import type { Row } from '@tanstack/react-table'
import { useUnit } from 'effector-react'
import { useQueryClient } from 'react-query'

import { useDateRangeContext } from '@/components/provider/date-range-provider.tsx'
import { TextField } from '@/components/ui/text-field.tsx'
import { useToast } from '@/components/ui/toast.tsx'
import { $currentCreator } from '@/models/creators.ts'
import type { StatisticsData } from '@/modules/overview/types.ts'
import { statisticsService } from '@/modules/statistics/onlyfans/service/statistics-service.ts'
import { handleError } from '@/utils/error'

export interface Props {
  row: Row<any | undefined>
}

export const EditLinkClicks: FC<Props> = ({ row }) => {
  const queryClient = useQueryClient()
  const { date } = useDateRangeContext()
  const [isDirty, setIsDirty] = useState<boolean>(false)
  const activeCreator = useUnit($currentCreator)
  const toast = useToast()

  const handleFocusEvent = async (event: FocusEvent<HTMLInputElement>) => {
    if (!isDirty || !activeCreator) return
    setIsDirty(false)
    const originalDate = row.original?.originalDate

    queryClient.setQueryData<StatisticsData>(
      statisticsService.getStatisticsKey(
        [activeCreator.id ?? 0],
        date.from!,
        date.to!,
      ),
      (oldData) => {
        const oldDataTyped = oldData as StatisticsData

        const oldEntry = oldDataTyped.data[row.original.key]
        const newData = oldDataTyped.data

        newData[row.original.key] = {
          ...oldEntry,
          linkClicks: Number(event.target.value),
        }

        return {
          ...oldDataTyped,
          data: newData,
        }
      },
    )

    statisticsService
      .updateLinkClicks(
        activeCreator?.id,
        originalDate,
        Number(event.target.value),
      )
      .then(() => toast.success('Successfully updated link clicks!'))
      .catch((error) => handleError(error, 'Failed to update link clicks'))
  }

  return (
    <TextField
      className="text-secondary-500 w-28"
      type="number"
      defaultValue={row.getValue('linkClicks')}
      onChange={() => setIsDirty(true)}
      onBlur={handleFocusEvent}
    />
  )
}
